import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Shield, Globe, Lock, ArrowRight, Terminal, KeyRound, Layers } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";

const HERO_BG = "https://images.unsplash.com/photo-1722080826167-4ea87368cbc5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxjeWJlciUyMHNlY3VyaXR5JTIwZGFyayUyMGNvZGV8ZW58MHx8fHwxNzc3NjYwNzkwfDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Top selling: fixed list of 6 most popular Autodesk products by slug
    const TOP_SLUGS = ["autocad", "revit", "3ds-max", "civil-3d", "inventor-professional", "fusion"];
    api.get("/products?category=Autodesk&limit=200")
      .then((r) => {
        const bySlug = {};
        r.data.products.forEach((p) => { bySlug[p.slug] = p; });
        const ordered = TOP_SLUGS.map((s) => bySlug[s]).filter(Boolean);
        setProducts(ordered);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#1E293B]">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/85 to-[#050505]" />
          <div className="absolute inset-0 bg-grid opacity-40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-28">
          <div className="chip-mono mb-6 fade-up">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00FF66] animate-pulse" />
            verified retail keys · instant delivery
          </div>
          <h1 className="font-heading font-bold text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-4xl fade-up" style={{ animationDelay: "60ms" }}>
            Genuine software<br />
            <span className="text-[#00FF66]">delivered</span> in seconds<span className="cursor-blink text-[#00FF66]">_</span>
          </h1>
          <p className="text-[#A1A1AA] text-lg mt-6 max-w-2xl fade-up" style={{ animationDelay: "120ms" }}>
            Original Windows, Office, and Antivirus license keys at fair prices.
            Pay, receive, activate — usually within 60 seconds.
          </p>
          <div className="flex flex-wrap gap-3 mt-8 fade-up" style={{ animationDelay: "180ms" }}>
            <Link data-testid="hero-cta-shop" to="/products" className="btn-primary">
              Browse catalog <ArrowRight className="w-4 h-4" />
            </Link>
            <Link data-testid="hero-cta-orders" to="/order-lookup" className="btn-ghost">
              Track an order
            </Link>
          </div>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6 max-w-3xl">
            {[
              { icon: Zap, label: "Instant delivery", val: "<60s" },
              { icon: Shield, label: "Genuine keys", val: "100%" },
              { icon: Globe, label: "Worldwide", val: "ship-anywhere" },
              { icon: Lock, label: "Secure", val: "stripe checkout" },
            ].map((s, i) => (
              <div key={i} className="border-l border-[#1E293B] pl-4">
                <s.icon className="w-4 h-4 text-[#00FF66]" />
                <div className="font-heading text-2xl font-bold mt-2">{s.val}</div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-[#71717A] mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-[#00FF66]">// catalog</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl mt-2">Pick your license</h2>
          </div>
          <Link to="/products" className="font-mono text-xs text-[#A1A1AA] hover:text-[#00FF66]">view all →</Link>
        </div>
        <div className="grid md:grid-cols-12 gap-4">
          <Link to="/products?category=Windows" className="surface surface-hover md:col-span-7 p-8 group min-h-[220px] relative overflow-hidden">
            <KeyRound className="w-6 h-6 text-[#00FF66] mb-3" />
            <h3 className="font-heading text-2xl font-semibold">Windows</h3>
            <p className="text-[#A1A1AA] text-sm mt-1">Win 10 / 11 — Home & Pro retail keys</p>
            <div className="font-mono text-[10px] tracking-widest text-[#00FF66] mt-6 opacity-0 group-hover:opacity-100 transition-opacity">EXPLORE_WINDOWS →</div>
          </Link>
          <Link to="/products?category=Office" className="surface surface-hover md:col-span-5 p-8 group min-h-[220px]">
            <Terminal className="w-6 h-6 text-[#00FF66] mb-3" />
            <h3 className="font-heading text-2xl font-semibold">Office</h3>
            <p className="text-[#A1A1AA] text-sm mt-1">Word, Excel, PowerPoint, Outlook</p>
          </Link>
          <Link to="/products?category=Antivirus" className="surface surface-hover md:col-span-4 p-8 group min-h-[200px]">
            <Shield className="w-6 h-6 text-[#00FF66] mb-3" />
            <h3 className="font-heading text-2xl font-semibold">Antivirus</h3>
            <p className="text-[#A1A1AA] text-sm mt-1">Bitdefender, Norton, Kaspersky</p>
          </Link>
          <Link to="/products?category=Autodesk" className="surface surface-hover md:col-span-4 p-8 group min-h-[200px] relative overflow-hidden">
            <Layers className="w-6 h-6 text-[#00FF66] mb-3" />
            <h3 className="font-heading text-2xl font-semibold">Autodesk</h3>
            <p className="text-[#A1A1AA] text-sm mt-1">AutoCAD · Revit · Maya · 41 products</p>
            <div className="font-mono text-[10px] tracking-widest text-[#00FF66] mt-4 opacity-0 group-hover:opacity-100 transition-opacity">1 YEAR SUBSCRIPTION →</div>
          </Link>
          <Link to="/products" className="surface surface-hover md:col-span-4 p-8 group min-h-[200px]">
            <Zap className="w-6 h-6 text-[#00FF66] mb-3" />
            <h3 className="font-heading text-2xl font-semibold">All software</h3>
            <p className="text-[#A1A1AA] text-sm mt-1">Browse the full catalog</p>
          </Link>
        </div>
      </section>

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-[#00FF66]">// featured</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl mt-2">Top selling licenses</h2>
          </div>
        </div>
        {loading ? (
          <div className="text-[#A1A1AA] font-mono text-sm">loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* Process */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="surface p-10 md:p-14 relative overflow-hidden">
          <div className="bg-grid absolute inset-0 opacity-30" />
          <div className="relative">
            <div className="font-mono text-xs uppercase tracking-widest text-[#00FF66]">// how it works</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl mt-2 mb-10">Three steps. Sixty seconds.</h2>
            <div className="grid md:grid-cols-3 gap-10">
              {[
                { n: "01", t: "Pick a license", d: "Choose from Windows, Office, Antivirus and more." },
                { n: "02", t: "Pay securely", d: "Stripe-encrypted checkout. Cards & wallets supported." },
                { n: "03", t: "Get your key", d: "Delivered on-screen and to your email — instantly." },
              ].map((s) => (
                <div key={s.n}>
                  <div className="font-mono text-[#00FF66] text-sm">{s.n}</div>
                  <h3 className="font-heading text-xl font-semibold mt-2">{s.t}</h3>
                  <p className="text-[#A1A1AA] text-sm mt-1">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
